/**
 * 充值转化（Alice 页面 firstRechargeStats 的复刻）：通用充值漏斗、领取后质量、入口、来源、人群与每日趋势。
 *
 * 背景：Alice 的「首充」是一次性的首充奖励活动，带 Alice 外观发放；本仓没有首充奖励，改成通用充值活动：
 *   「当前可领取」换成当前生效活动数，「来源」换成下单商品，「人群」是首次充值与复购，档位是活动批次，
 *   「外观履约」换成订单履约（订单已到账、待到账、重试中），「跳转失败」没有对应物（本仓没有外部跳转）而去掉。
 * 设计意图：版式、六张指标卡、说明条、各表列顺序与 Alice 一致；数据只来自服务端：订单读支付账本，
 *   漏斗读控制台上报的事件（控制台尚未接入上报前，曝光、点击、到达都是 0，订单与支付不受影响）。
 *   领取后「使用」来自控制面的首次成功用量，查不到时显示「—」，不拿 0 冒充。
 * 关键约束：统计时区是 UTC；漏斗只读，不做任何本地估算。
 */
import { useState } from 'react'
import { campaignApi } from '@ap/api-client/admin-campaign'
import { AG_campaign as G, errorTextC } from '@ap/i18n/admin-campaign'
import { intlTag, useI18n } from '@ap/i18n'
import { fmtDateTime, navigate, useLoad } from '@ap/ui'
import { OpsPage } from '../../alice/kit.tsx'
import { ToolSelect, fmtNum, fmtUsdStr, pct } from '../../alice/kit-campaign.tsx'

const DAYS = [7, 30, 90, 180, 365]

function Mini({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="fr-admin-mini">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note ?? ''}</small>
    </div>
  )
}

export function FirstRechargeStats() {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const [days, setDays] = useState(30)
  const { data, error, reload } = useLoad(() => campaignApi.funnel(days), [days])
  const num = (v: number | undefined) => fmtNum(v ?? 0, tag)
  const money = (v: string | number | undefined) => fmtUsdStr(v ?? 0, tag)
  const dash = t(G.common.dash)
  const s = data?.summary
  const label = (name: string) => (name in G.funnel.labels ? t(G.funnel.labels[name]) : name)

  const funnel = data?.funnel ?? []
  const funnelMax = Math.max(1, ...funnel.map((f) => f.users))

  const failures = data?.events.failures ?? {}
  const orderFailures = Object.entries(failures)
    .filter(([k]) => k.startsWith('order_create_failed:'))
    .reduce((sum, [, n]) => sum + n, 0)
  const ret = data?.retention
  const usage = (r: { users: number; rate: string } | null | undefined) => (r ? { v: pct(r.rate), n: t(G.funnel.mini.people, { n: num(r.users) }) } : { v: dash, n: '' })
  const u1 = usage(ret?.usage_1d)
  const u7 = usage(ret?.usage_7d)
  const u30 = usage(ret?.usage_30d)

  const segments = [
    ...(data?.audiences ?? []).map((a) => ({ ...a, name: t(G.funnel.segmentAudience, { name: label(a.name) }) })),
    ...(data?.tiers ?? []).map((a) => ({ ...a, name: t(G.funnel.segmentBatch, { name: a.name === 'none' ? t(G.funnel.noBatch) : a.name }) })),
  ]

  const q = data?.data_quality
  const r = data?.risk_signals
  const qualityVars = {
    missing: num(q?.orders_missing_attribution),
    mismatch: num(q?.amount_mismatch),
    stuck: num(q?.fulfillment_attention_needed),
    multi: num(r?.users_with_multiple_orders),
    stale: num(r?.stale_pending_orders),
    late: num(r?.late_paid_after_close),
    refunded: num(r?.refunded_after_claim),
  }
  const ful = data?.fulfillment

  return (
    <OpsPage
      eyebrow={t(G.funnel.eyebrow)}
      title={t(G.funnel.title)}
      desc={t(G.funnel.desc)}
      actions={
        <div className="fr-admin-toolbar">
          <ToolSelect
            value={String(days)}
            onChange={(e) => setDays(Number(e.target.value))}
            options={DAYS.map((d) => ({ value: String(d), label: t(G.funnel.days(d)) }))}
            style={{ fontSize: 12, padding: '7px 28px 7px 10px' }}
          />
          <button className="btn btn-secondary btn-sm" type="button" onClick={reload}>
            {t(G.common.refresh)}
          </button>
        </div>
      }
    >
      <div className="campaign-stats-grid">
        <div className="campaign-stat">
          <span>{t(G.funnel.eligible)}</span>
          <strong>{data ? num(data.eligibility.active_campaigns) : dash}</strong>
          <small>{data ? t(G.funnel.eligibleNote, { name: data.eligibility.primary_name ?? t(G.funnel.noPrimary), n: num(data.eligibility.claimed) }) : ''}</small>
        </div>
        <div className="campaign-stat">
          <span>{t(G.funnel.exposed)}</span>
          <strong>{s ? num(s.entry_exposed_users) : dash}</strong>
          <small>{s ? t(G.funnel.clickRate, { p: Number(s.entry_click_rate).toFixed(2) }) : ''}</small>
        </div>
        <div className="campaign-stat">
          <span>{t(G.funnel.checkout)}</span>
          <strong>{s ? num(s.checkout_users) : dash}</strong>
          <small>{s ? t(G.funnel.payRate, { p: Number(s.checkout_pay_rate).toFixed(2), n: num(s.checkout_orders) }) : ''}</small>
        </div>
        <div className="campaign-stat activity">
          <span>{t(G.funnel.claimed)}</span>
          <strong>{s ? num(s.claimed_users) : dash}</strong>
          <small>{s ? t(G.funnel.claimRate, { p: Number(s.exposure_claim_rate).toFixed(2) }) : ''}</small>
        </div>
        <div className="campaign-stat">
          <span>{t(G.funnel.revenue)}</span>
          <strong>{s ? money(s.net_paid_usd) : dash}</strong>
          <small>{s ? t(G.funnel.aov, { a: Number(s.average_paid_usd).toFixed(2), r: Number(s.refunded_usd).toFixed(2) }) : ''}</small>
        </div>
        <div className="campaign-stat activity">
          <span>{t(G.funnel.bonusValue)}</span>
          <strong>{s ? money(s.bonus_value_usd) : dash}</strong>
          <small>{s ? t(G.funnel.bonus, { n: num(s.bonus_petals), m: num(s.refunded_bonus_petals) }) : ''}</small>
        </div>
      </div>
      <div className="campaign-cost-note">{error ? t(G.funnel.noteFailed, { msg: errorTextC(error, locale) }) : data ? t(G.funnel.note) : t(G.funnel.noteLoading)}</div>

      <div className="fr-admin-grid">
        <section className="fr-admin-card">
          <div className="fr-admin-card-head">
            <div>
              <strong>{t(G.funnel.funnelTitle)}</strong>
              <span>{t(G.funnel.funnelSub)}</span>
            </div>
          </div>
          <div className="fr-admin-funnel">
            {funnel.length === 0 ? (
              <div className="empty">{t(G.funnel.funnelEmpty)}</div>
            ) : (
              funnel.map((f, i) => {
                const prev = i ? funnel[i - 1].users : f.users
                // 前几步来自前端上报的事件，后几步来自服务端订单；入口还没接入上报时后一步会多于前一步，此时不显示超过 100% 的转化率
                const step = prev ? (f.users / prev) * 100 : 0
                const width = (f.users / funnelMax) * 100
                return (
                  <div className="fr-admin-funnel-row" key={f.key}>
                    <span>{f.key in G.funnel.steps ? t(G.funnel.steps[f.key]) : f.key}</span>
                    <div className="fr-admin-funnel-track">
                      <div className="fr-admin-funnel-bar" style={{ width: `${Math.max(0, Math.min(100, width)).toFixed(2)}%` }} />
                    </div>
                    <span className="fr-admin-funnel-count">{num(f.users)}</span>
                    <span className="fr-admin-funnel-rate">{i ? (step > 100 ? dash : `${step.toFixed(1)}%`) : t(G.funnel.base)}</span>
                  </div>
                )
              })
            )}
          </div>
        </section>
        <section className="fr-admin-card">
          <div className="fr-admin-card-head">
            <div>
              <strong>{t(G.funnel.qualityTitle)}</strong>
              <span>{t(G.funnel.qualitySub)}</span>
            </div>
          </div>
          <div className="fr-admin-mini-grid">
            <Mini label={t(G.funnel.mini.orderFailed)} value={data ? num(orderFailures) : dash} note={t(G.funnel.mini.orderFailedNote)} />
            <Mini label={t(G.funnel.mini.cohort)} value={ret ? num(ret.cohort) : dash} note={t(G.funnel.mini.cohortNote)} />
            <Mini label={t(G.funnel.mini.usage1)} value={u1.v} note={u1.n} />
            <Mini label={t(G.funnel.mini.usage7)} value={u7.v} note={u7.n} />
            <Mini label={t(G.funnel.mini.usage30)} value={u30.v} note={u30.n} />
            <Mini label={t(G.funnel.mini.repeat7)} value={ret ? pct(ret.repeat_pay_7d.rate) : dash} note={ret ? t(G.funnel.mini.people, { n: num(ret.repeat_pay_7d.users) }) : ''} />
            <Mini label={t(G.funnel.mini.repeat30)} value={ret ? pct(ret.repeat_pay_30d.rate) : dash} note={ret ? t(G.funnel.mini.people, { n: num(ret.repeat_pay_30d.users) }) : ''} />
            <Mini label={t(G.funnel.mini.pageCheckout)} value={s ? pct(s.page_checkout_rate) : dash} note={s ? t(G.funnel.mini.pageViewed, { n: num(s.page_view_users) }) : ''} />
            <Mini label={t(G.funnel.mini.pending)} value={s ? num(s.pending_orders) : dash} note={t(G.funnel.mini.pendingNote)} />
          </div>
        </section>
      </div>

      <div className="fr-admin-tables-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14, marginTop: 14 }}>
        <section className="fr-admin-card">
          <div className="fr-admin-card-head">
            <div>
              <strong>{t(G.funnel.placementTitle)}</strong>
              <span>{t(G.funnel.placementSub)}</span>
            </div>
          </div>
          <div className="fr-admin-table-scroll" style={{ overflow: 'auto', maxHeight: 320 }}>
            <table>
              <thead>
                <tr>
                  <th>{t(G.funnel.placementCols.name)}</th>
                  <th>{t(G.funnel.placementCols.exposed)}</th>
                  <th>{t(G.funnel.placementCols.clicked)}</th>
                  <th>{t(G.funnel.placementCols.viewed)}</th>
                  <th>{t(G.funnel.placementCols.rate)}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.placements ?? []).map((p) => (
                  <tr key={p.name}>
                    <td>{label(p.name)}</td>
                    <td>{num(p.exposed_users)}</td>
                    <td>{num(p.clicked_users)}</td>
                    <td>{num(p.page_view_users)}</td>
                    <td>{pct(p.click_rate)}</td>
                  </tr>
                ))}
                {data && data.placements.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty">
                      {t(G.funnel.placementEmpty)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        <section className="fr-admin-card">
          <div className="fr-admin-card-head">
            <div>
              <strong>{t(G.funnel.sourceTitle)}</strong>
              <span>{t(G.funnel.sourceSub)}</span>
            </div>
          </div>
          <div className="fr-admin-table-scroll" style={{ overflow: 'auto', maxHeight: 320 }}>
            <table>
              <thead>
                <tr>
                  <th>{t(G.funnel.sourceCols.name)}</th>
                  <th>{t(G.funnel.sourceCols.users)}</th>
                  <th>{t(G.funnel.sourceCols.orders)}</th>
                  <th>{t(G.funnel.sourceCols.paid)}</th>
                  <th>{t(G.funnel.sourceCols.rate)}</th>
                  <th>{t(G.funnel.sourceCols.revenue)}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.sources ?? []).map((x) => (
                  <tr key={x.name}>
                    <td>{x.name}</td>
                    <td>{num(x.users)}</td>
                    <td>{num(x.orders)}</td>
                    <td>{num(x.paid_orders)}</td>
                    <td>{pct(x.conversion_rate)}</td>
                    <td>{money(x.paid_usd)}</td>
                  </tr>
                ))}
                {data && data.sources.length === 0 && (
                  <tr>
                    <td colSpan={6} className="empty">
                      {t(G.funnel.sourceEmpty)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        <section className="fr-admin-card">
          <div className="fr-admin-card-head">
            <div>
              <strong>{t(G.funnel.segmentTitle)}</strong>
              <span>{t(G.funnel.segmentSub)}</span>
            </div>
          </div>
          <div className="fr-admin-table-scroll" style={{ overflow: 'auto', maxHeight: 320 }}>
            <table>
              <thead>
                <tr>
                  <th>{t(G.funnel.segmentCols.name)}</th>
                  <th>{t(G.funnel.segmentCols.users)}</th>
                  <th>{t(G.funnel.segmentCols.revenue)}</th>
                  <th>{t(G.funnel.segmentCols.bonus)}</th>
                </tr>
              </thead>
              <tbody>
                {segments.map((x) => (
                  <tr key={x.name}>
                    <td>{x.name}</td>
                    <td>{num(x.users)}</td>
                    <td>{money(x.paid_usd)}</td>
                    <td>{num(x.bonus_petals)}</td>
                  </tr>
                ))}
                {data && segments.length === 0 && (
                  <tr>
                    <td colSpan={4} className="empty">
                      {t(G.funnel.segmentEmpty)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="fr-admin-grid">
        <section className="fr-admin-card">
          <div className="fr-admin-card-head">
            <div>
              <strong>{t(G.funnel.fulfillTitle)}</strong>
              <span>{t(G.funnel.fulfillSub)}</span>
            </div>
          </div>
          <div className="fr-admin-mini-grid">
            <Mini label={t(G.funnel.fulfill.rate)} value={ful ? pct(ful.success_rate) : dash} note={t(G.funnel.fulfill.rateNote)} />
            <Mini label={t(G.funnel.fulfill.fulfilled)} value={ful ? num(ful.fulfilled) : dash} note={t(G.funnel.fulfill.fulfilledNote)} />
            <Mini label={t(G.funnel.fulfill.pending)} value={ful ? num(ful.pending) : dash} note={t(G.funnel.fulfill.pendingNote)} />
            <Mini label={t(G.funnel.fulfill.retrying)} value={ful ? num(ful.retrying) : dash} note={t(G.funnel.fulfill.retryingNote)} />
            <Mini label={t(G.funnel.fulfill.refunded)} value={ful ? num(ful.refunded) : dash} note={t(G.funnel.fulfill.refundedNote)} />
            <Mini label={t(G.funnel.fulfill.mismatch)} value={ful ? num(ful.amount_mismatch) : dash} note={t(G.funnel.fulfill.mismatchNote)} />
          </div>
          {q && (
            <div className={`campaign-cost-note fr-admin-quality ${q.healthy ? 'ok' : 'warn'}`}>
              {t(q.healthy ? G.funnel.qualityHealthy : G.funnel.qualityWarn, qualityVars)}
            </div>
          )}
        </section>
        <section className="fr-admin-card">
          <div className="fr-admin-card-head">
            <div>
              <strong>{t(G.funnel.dailyTitle)}</strong>
              <span>{t(G.funnel.dailySub)}</span>
            </div>
          </div>
          <div className="fr-admin-table-scroll" style={{ overflow: 'auto', maxHeight: 320 }}>
            <table>
              <thead>
                <tr>
                  <th>{t(G.funnel.dailyCols.date)}</th>
                  <th>{t(G.funnel.dailyCols.exposed)}</th>
                  <th>{t(G.funnel.dailyCols.viewed)}</th>
                  <th>{t(G.funnel.dailyCols.orders)}</th>
                  <th>{t(G.funnel.dailyCols.paidUsers)}</th>
                  <th>{t(G.funnel.dailyCols.revenue)}</th>
                  <th>{t(G.funnel.dailyCols.claimed)}</th>
                  <th>{t(G.funnel.dailyCols.bonus)}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.daily ?? []).map((d) => (
                  <tr key={d.date}>
                    <td>{d.date}</td>
                    <td>{num(d.entry_exposed_users)}</td>
                    <td>{num(d.page_view_users)}</td>
                    <td>
                      {num(d.checkout_users)} / {num(d.orders)}
                    </td>
                    <td>{num(d.paid_users)}</td>
                    <td>{money(d.paid_usd)}</td>
                    <td>{num(d.claimed_users)}</td>
                    <td>{num(d.bonus_petals)}</td>
                  </tr>
                ))}
                {data && data.daily.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty">
                      {t(G.funnel.dailyEmpty)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="fr-admin-card" style={{ marginTop: 14 }}>
        <div className="fr-admin-card-head">
          <div>
            <strong>{t(G.funnel.claimsTitle)}</strong>
            <span>{t(G.funnel.claimsSub)}</span>
          </div>
        </div>
        <div className="fr-admin-table-scroll" style={{ overflow: 'auto', maxHeight: 420 }}>
          <table>
            <thead>
              <tr>
                <th>{t(G.funnel.claimsCols.time)}</th>
                <th>{t(G.funnel.claimsCols.user)}</th>
                <th>{t(G.funnel.claimsCols.audience)}</th>
                <th>{t(G.funnel.claimsCols.source)}</th>
                <th>{t(G.funnel.claimsCols.order)}</th>
                <th>{t(G.funnel.claimsCols.amount)}</th>
                <th>{t(G.funnel.claimsCols.bonus)}</th>
                <th>{t(G.funnel.claimsCols.fulfillment)}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent_claims ?? []).map((c) => (
                <tr key={c.order_id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(c.claimed_at, locale)}</td>
                  <td title={c.account_email || c.account_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/accounts/${c.account_id}`)}>
                    {c.account_name}
                  </td>
                  <td>{label(c.audience)}</td>
                  <td>{c.source}</td>
                  <td className="mono">{c.order_id.slice(0, 8)}</td>
                  <td>{money(c.amount_usd)}</td>
                  <td>{num(c.bonus_petals)}</td>
                  <td title={c.fulfillment_error}>{c.fulfillment_status in G.funnel.fulfillStatus ? t(G.funnel.fulfillStatus[c.fulfillment_status]) : c.fulfillment_status}</td>
                </tr>
              ))}
              {data && data.recent_claims.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty">
                    {t(G.funnel.claimsEmpty)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </OpsPage>
  )
}
